import { FilterList } from '@mui/icons-material';
import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTranslatedSnackbar } from 'src/hooks/useTranslatedSnackbar';
import { useParams } from 'react-router';
import { visitApi } from 'src/api/visit';
import { useUserContext } from 'src/auth/useUserContext';
import { useCurrentVisitContext } from '../hooks/useCurrentVisitContext';

import ProfileVisitHistoryFilters, {
  EMPTY_HISTORY_FILTERS,
  OWNER_NOTE_STATUSES,
  ProfileHistoryFilters,
  toHistoryQuery,
} from './ProfileVisitHistoryFilters';
import ProfileVisitsHistoryTable from './ProfileVisitsHistoryTable';
import ProfileVisitsHistoryTableSkeleton from './ProfileVisitsHistoryTableSkeleton';
import { NOTE_STATUSES } from './VisitHistoryFilters';
import { useTable } from 'src/components/table';
import { CARD_HEADER_SX, CARD_HEADER_TITLE_PROPS } from 'src/theme/cardHeader';
import { TAB_TABLE_DEFAULT_ROWS_PER_PAGE } from 'src/theme/tableContainer';

export const ProfileVisitsHistoryContent = () => {
  const { t } = useTranslation();
  const { isUserRole } = useUserContext();
  const { enqueueTranslated } = useTranslatedSnackbar();
  const { data: currentVisitData } = useCurrentVisitContext();
  const { profileId } = useParams<{ profileId: string }>();
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: TAB_TABLE_DEFAULT_ROWS_PER_PAGE,
  });

  const [filters, setFilters] =
    useState<ProfileHistoryFilters>(EMPTY_HISTORY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const query = toHistoryQuery(filters);

  const { data, isLoading, isError } = useQuery(
    ['profileVisitsHistory', profileId, page, rowsPerPage, query],
    () =>
      visitApi.visitControllerGetVisitHistory({
        profileId: profileId as string,
        page,
        limit: rowsPerPage,
        ...query,
      }),
    {
      retry: 1,
      enabled: Boolean(isUserRole),
      keepPreviousData: true,
    },
  );

  const {
    data: previewData,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useQuery(
    ['profileVisitsHistoryPreview', profileId, page, rowsPerPage, query],
    () =>
      visitApi.visitControllerGetProfileVisitHistoryPreview({
        profileId: profileId as string,
        page,
        limit: rowsPerPage,
        ...query,
      }),
    {
      retry: 1,
      enabled: !Boolean(isUserRole) && Boolean(currentVisitData),
      keepPreviousData: true,
    },
  );

  useEffect(() => {
    if (
      (!data && !isLoading && isError) ||
      (!previewData && !isPreviewLoading && isPreviewError)
    ) {
      enqueueTranslated('snackbar.petsyHistoryFetchError', {
        variant: 'error',
      });
    }
  }, [
    data,
    isLoading,
    isError,
    enqueueTranslated,
    previewData,
    isPreviewLoading,
    isPreviewError,
  ]);

  const history = data?.data ?? previewData?.data;

  const handleFiltersChange = (next: ProfileHistoryFilters) => {
    setFilters(next);
    // A narrower list has fewer pages; staying on page 3 of the old one would
    // show an empty table.
    onChangePage(null, 0);
  };

  const hasFilters =
    Boolean(filters.startDate || filters.endDate) ||
    Boolean(filters.closedFrom || filters.closedTo) ||
    Boolean(filters.professionalName?.trim()) ||
    filters.statuses.length > 0 ||
    filters.noteStatuses.length > 0;

  return !isLoading || !isPreviewLoading ? (
    <>
      <Stack sx={CARD_HEADER_SX}>
        <Typography {...CARD_HEADER_TITLE_PROPS}>
          {t('nav.visitHistory')}
        </Typography>
        {/* Labelled, not just an icon: people using this said they could not
            tell there were filters here at all. */}
        <Button
          size="small"
          variant="outlined"
          startIcon={<FilterList />}
          data-testid="history-filters-toggle"
          onClick={() => setFiltersOpen((open) => !open)}
          color={filtersOpen || hasFilters ? 'primary' : 'inherit'}
        >
          {t('visit.historyFiltersTooltip')}
        </Button>
      </Stack>
      <ProfileVisitHistoryFilters
        value={filters}
        onChange={handleFiltersChange}
        isOpen={filtersOpen}
        // This card is the owner's own view when `isUserRole`, and the
        // professional's read-only preview otherwise. Drafts exist only for the
        // latter — see OWNER_NOTE_STATUSES.
        noteStatusOptions={isUserRole ? OWNER_NOTE_STATUSES : NOTE_STATUSES}
        showProfessionalFilter
      />
      <ProfileVisitsHistoryTable
        data={history?.data ?? []}
        page={page}
        rowsPerPage={rowsPerPage}
        total={history?.total ?? 0}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        emptyMessage={
          hasFilters ? String(t('visit.historyFilterEmpty')) : undefined
        }
      />
    </>
  ) : (
    <>
      <Stack sx={CARD_HEADER_SX}>
        <Skeleton variant="rounded" height="24px" width="104px" />
      </Stack>
      <ProfileVisitsHistoryTableSkeleton />
    </>
  );
};
